import {
  AlertCircle,
  ArrowDown,
  ArrowUp,
  Check,
  ExternalLink,
  Globe2,
  Monitor,
  Plus,
  RotateCcw,
  Smartphone,
  Tablet,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { api, type WebsiteDraft, type WebsiteSection, type WebsiteVersionSummary, type WebsiteWorkspace } from "../api.js";
import { apiErrorMessage } from "../../api/client.js";
import { Button } from "../../design/components/Button.js";
import { ResponsiveSheet } from "../../design/components/ResponsiveSheet.js";
import { PreviewFrame, type WorkspacePreviewProps } from "./PreviewFrame.js";
import { LocalSegmentedControl } from "./previewPrimitives.js";

const pageDefinitions = [
  { label: "Home", key: "home", type: "HERO", description: "The main conference introduction.", required: true },
  { label: "About", key: "about", type: "ABOUT", description: "Purpose, audience, and committee context.", required: false },
  { label: "Important dates", key: "dates", type: "DATES", description: "Conference dates from the saved event.", required: false },
  { label: "Call for papers", key: "call-for-papers", type: "CALL_FOR_PAPERS", description: "Author guidance and the live submission entry point.", required: false },
  { label: "Speakers", key: "speakers", type: "SPEAKERS", description: "Invited speakers and featured contributors.", required: false },
  { label: "Programme", key: "programme", type: "PROGRAMME", description: "The published schedule, kept in sync automatically.", required: false },
  { label: "Registration", key: "registration", type: "REGISTRATION", description: "Attendee guidance and the live registration entry point.", required: false },
  { label: "Venue", key: "venue", type: "VENUE", description: "Location and arrival information.", required: false },
  { label: "Sponsors", key: "sponsors", type: "SPONSORS", description: "Approved sponsor acknowledgements.", required: false },
  { label: "Exhibitors", key: "exhibitors", type: "EXHIBITORS", description: "Approved exhibitor information.", required: false },
  { label: "FAQ", key: "faq", type: "FAQ", description: "Practical answers for participants.", required: false },
  { label: "Contact", key: "contact", type: "CONTACT", description: "Official contact information.", required: false },
] as const;

type PageDefinition = (typeof pageDefinitions)[number];
type PreviewDevice = "desktop" | "tablet" | "phone";
const emptyDraft: Pick<WebsiteDraft, "template_key" | "sections" | "responsive"> = {
  template_key: "MINIMAL",
  sections: [],
  responsive: { hero_alignment: "LEFT", density: "COMFORTABLE" },
};

function publicationLabel(version: WebsiteVersionSummary) {
  if (version.status === "PUBLISHED" && version.unpublished_at) return "SUPERSEDED";
  return version.status;
}

export function WebsiteWorkspacePreview({ conference }: WorkspacePreviewProps) {
  const [editorOpen, setEditorOpen] = useState(false);
  const [confirmation, setConfirmation] = useState<"publish" | "unpublish" | null>(null);
  const [restoreVersion, setRestoreVersion] = useState<WebsiteVersionSummary | null>(null);
  const [page, setPage] = useState<PageDefinition>(pageDefinitions[0]);
  const [pane, setPane] = useState("Preview");
  const [device, setDevice] = useState<PreviewDevice>("desktop");
  const [workspace, setWorkspace] = useState<WebsiteWorkspace | null>(null);
  const [draft, setDraft] = useState(emptyDraft);
  const [title, setTitle] = useState("Home");
  const [body, setBody] = useState("");
  const [items, setItems] = useState("");
  const [ctaLabel, setCtaLabel] = useState("");
  const [ctaUrl, setCtaUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [acting, setActing] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    const nextWorkspace = await api.websiteWorkspace(conference.id, conference.organization_id);
    setWorkspace(nextWorkspace);
    const source = nextWorkspace.draft ?? nextWorkspace.published;
    setDraft(source ? { template_key: source.template_key, sections: source.sections, responsive: source.responsive } : emptyDraft);
    return nextWorkspace;
  }, [conference.id, conference.organization_id]);

  useEffect(() => {
    let active = true;
    load().catch((reason) => { if (active) setError(apiErrorMessage(reason, "The website workspace could not be loaded.")); });
    return () => { active = false; };
  }, [load]);

  const selectedSection = useMemo(
    () => draft.sections.find((section) => section.stable_key === page.key),
    [draft.sections, page.key],
  );
  const selectedIndex = selectedSection
    ? draft.sections.findIndex((section) => section.stable_key === selectedSection.stable_key)
    : -1;
  const beginEdit = () => {
    setTitle(selectedSection?.heading || page.label);
    setBody(selectedSection?.body || "");
    setItems(selectedSection?.items?.join("\n") || "");
    setCtaLabel(selectedSection?.cta_label || "");
    setCtaUrl(selectedSection?.cta_url || "");
    setMessage("");
    setError("");
    setEditorOpen(true);
  };

  const save = async () => {
    const cleanTitle = title.trim();
    if (!cleanTitle) { setError("Add a page title before saving."); return; }
    if (ctaUrl.trim() && !ctaUrl.trim().startsWith("https://")) {
      setError("Custom links must use a complete HTTPS address.");
      return;
    }
    const section: WebsiteSection = {
      ...selectedSection,
      stable_key: page.key,
      type: page.type,
      heading: cleanTitle,
      body: body.trim(),
      items: items.split("\n").map((item) => item.trim()).filter(Boolean),
      cta_label: ctaLabel.trim(),
      cta_url: ctaUrl.trim(),
    };
    const nextSections = selectedSection
      ? draft.sections.map((item) => item.stable_key === section.stable_key ? section : item)
      : [...draft.sections, section];
    const payload = { template_key: draft.template_key, sections: nextSections, responsive: draft.responsive };
    setSaving(true);
    setError("");
    try {
      await api.saveWebsiteDraft(conference.id, conference.organization_id, payload);
      await load();
      setMessage(`${page.label} page saved as a private draft.`);
      setEditorOpen(false);
    } catch (reason) { setError(apiErrorMessage(reason, "The website draft could not be saved.")); }
    finally { setSaving(false); }
  };

  const moveSelected = (offset: -1 | 1) => {
    if (selectedIndex < 0) return;
    const target = selectedIndex + offset;
    if (target < 0 || target >= draft.sections.length) return;
    const nextSections = [...draft.sections];
    [nextSections[selectedIndex], nextSections[target]] = [nextSections[target], nextSections[selectedIndex]];
    setDraft((current) => ({ ...current, sections: nextSections }));
    setMessage("Page order changed. Save the page to keep this order.");
  };

  const removePage = async () => {
    if (!selectedSection || page.required || draft.sections.length === 1) return;
    setSaving(true);
    setError("");
    try {
      await api.saveWebsiteDraft(conference.id, conference.organization_id, {
        template_key: draft.template_key,
        sections: draft.sections.filter((section) => section.stable_key !== selectedSection.stable_key),
        responsive: draft.responsive,
      });
      await load();
      setEditorOpen(false);
      setMessage(`${page.label} page removed from the new private draft.`);
    } catch (reason) { setError(apiErrorMessage(reason, "The page could not be removed.")); }
    finally { setSaving(false); }
  };

  const changePublication = async () => {
    if (!workspace || !confirmation) return;
    const website = confirmation === "publish" ? workspace.draft : workspace.published;
    if (!website) return;
    setActing(true);
    setError("");
    try {
      if (confirmation === "publish") {
        await api.publishWebsite(conference.id, conference.organization_id, website.id);
        await load();
        setMessage("Website published.");
      } else {
        await api.unpublishWebsite(conference.id, conference.organization_id, website.id);
        await load();
        setMessage("Website unpublished. The saved content remains available for editing.");
      }
      setConfirmation(null);
    } catch (reason) { setError(apiErrorMessage(reason, `The website could not be ${confirmation === "publish" ? "published" : "unpublished"}.`)); }
    finally { setActing(false); }
  };

  const restore = async () => {
    if (!restoreVersion) return;
    setActing(true);
    setError("");
    try {
      const sourceVersion = restoreVersion.version;
      await api.restoreWebsiteVersion(conference.id, conference.organization_id, restoreVersion.id);
      await load();
      setRestoreVersion(null);
      setMessage(`Version ${sourceVersion} restored as a new private draft.`);
    } catch (reason) { setError(apiErrorMessage(reason, "This website version could not be restored.")); }
    finally { setActing(false); }
  };

  const isLive = Boolean(workspace?.published);
  const previewAction = page.type === "CALL_FOR_PAPERS"
    ? "Submission entry appears here only while submissions are open."
    : page.type === "REGISTRATION"
      ? "Registration entry appears here only while a category is open."
      : page.type === "PROGRAMME"
        ? "The published programme appears here automatically."
        : null;

  return <PreviewFrame
    title="Conference website"
    description="Build the public conference home, connect live participant entry points, and publish an immutable version."
    operationalNote="Publishing the website does not open submissions, registration, or release the programme. Those controls remain separate."
    action={<Button onClick={beginEdit}><Plus size={16} />{selectedSection ? "Edit page" : "Add page"}</Button>}
  >
    {message ? <div className="website-draft-status" role="status"><Check size={17} aria-hidden="true" />{message}</div> : null}
    {error && !editorOpen && !confirmation && !restoreVersion ? <p className="form-error" role="alert">{error}</p> : null}
    <LocalSegmentedControl label="Website builder view" options={["Pages", "Preview", "Publication"]} value={pane} onChange={setPane} />
    <section className={`website-builder is-pane-${pane.toLowerCase()}`} aria-label="Website builder">
      <nav aria-label="Website pages">
        <div className="website-pages-heading"><div><h2>Pages</h2><span>{draft.sections.length} configured</span></div><p>Live blocks stay connected to the conference workflow.</p></div>
        {pageDefinitions.map((item) => {
          const saved = draft.sections.some((section) => section.stable_key === item.key);
          return <button type="button" aria-current={page.key === item.key ? "page" : undefined} onClick={() => setPage(item)} key={item.key}>
            <span className="website-page-name">{item.label}<small>{saved ? "Configured" : item.required ? "Required" : "Optional"}</small></span>
            {saved ? <Check size={15} aria-hidden="true" /> : <Plus size={15} aria-hidden="true" />}
          </button>;
        })}
      </nav>
      <section className="website-browser" aria-label="Website page preview" data-device={device}>
        <div className="website-preview-toolbar">
          <span><Globe2 size={16} />{workspace?.public_path || `/${conference.public_slug || "conference"}`}</span>
          <div role="group" aria-label="Preview device">
            <button type="button" aria-label="Desktop preview" aria-pressed={device === "desktop"} onClick={() => setDevice("desktop")}><Monitor size={16} /></button>
            <button type="button" aria-label="Tablet preview" aria-pressed={device === "tablet"} onClick={() => setDevice("tablet")}><Tablet size={16} /></button>
            <button type="button" aria-label="Phone preview" aria-pressed={device === "phone"} onClick={() => setDevice("phone")}><Smartphone size={16} /></button>
          </div>
        </div>
        <div className="website-preview-stage">
          <article className={draft.responsive.hero_alignment === "CENTER" ? "is-hero-centered" : ""} data-template={draft.template_key.toLowerCase()}>
            <header><strong>{conference.short_name || conference.name}</strong><span>Conference website</span></header>
            <main>
              <small>{page.type.replaceAll("_", " ")}</small>
              <h2>{selectedSection?.heading || page.label}</h2>
              <p>{selectedSection?.body || page.description}</p>
              {selectedSection?.items?.length ? <ul>{selectedSection.items.map((item) => <li key={item}>{item}</li>)}</ul> : null}
              {previewAction ? <div className="website-live-block"><Globe2 size={15} aria-hidden="true" /><span>{previewAction}</span></div> : null}
              {selectedSection?.cta_label ? <span className="website-preview-cta">{selectedSection.cta_label}</span> : null}
              {!selectedSection ? <><span className="website-placeholder" /><span className="website-placeholder is-short" /></> : null}
            </main>
          </article>
        </div>
      </section>
      <aside className="website-publish" aria-label="Publication settings">
        <div className="website-publication-heading"><div><h2>Publication</h2><span className={`website-live-badge${isLive ? " is-live" : ""}`}>{isLive ? "Live" : "Private"}</span></div>{isLive && workspace ? <a href={workspace.public_path} target="_blank" rel="noreferrer">View live site <ExternalLink size={14} aria-hidden="true" /></a> : null}</div>
        <dl><div><dt>Draft version</dt><dd>{workspace?.draft ? `Version ${workspace.draft.version}` : "No current draft"}</dd></div><div><dt>Live version</dt><dd>{workspace?.published ? `Version ${workspace.published.version}` : "Not published"}</dd></div><div><dt>Selected page</dt><dd>{selectedSection ? "Configured" : "Not configured"}</dd></div><div><dt>Template</dt><dd>{draft.template_key.toLocaleLowerCase()}</dd></div></dl>
        <div className="website-readiness"><h3>{workspace?.readiness.ready ? "Ready to publish" : "Before publishing"}</h3>{workspace?.readiness.ready ? <p className="is-ready"><Check size={15} aria-hidden="true" />Required website content is complete.</p> : null}{workspace?.readiness.blockers.map((item) => <p className="is-blocker" key={item}><AlertCircle size={15} aria-hidden="true" />{item}</p>)}{workspace?.readiness.warnings.map((item) => <p className="is-warning" key={item}><AlertCircle size={15} aria-hidden="true" />{item}</p>)}</div>
        <div className="website-publication-actions"><Button onClick={() => setConfirmation("publish")} disabled={!workspace?.draft || !workspace.readiness.ready}>Publish website</Button>{workspace?.published ? <Button variant="secondary" onClick={() => setConfirmation("unpublish")}>Unpublish</Button> : null}</div>
        {workspace?.versions.length ? <div className="website-version-history"><h3>Version history</h3><ol>{workspace.versions.slice(0, 8).map((version) => <li key={version.id}><span><b>Version {version.version}</b><small>{publicationLabel(version)}</small></span>{version.status !== "DRAFT" ? <button type="button" onClick={() => setRestoreVersion(version)} aria-label={`Restore version ${version.version}`}><RotateCcw size={13} aria-hidden="true" />Restore</button> : null}</li>)}</ol></div> : null}
      </aside>
    </section>
    <ResponsiveSheet open={editorOpen} title={`${selectedSection ? "Edit" : "Add"} ${page.label}`} onClose={() => setEditorOpen(false)} footer={<div className="preview-sheet-actions website-editor-actions">{selectedSection && !page.required && draft.sections.length > 1 ? <Button variant="danger" onClick={removePage} disabled={saving}>Remove page</Button> : null}<Button variant="secondary" onClick={() => setEditorOpen(false)}>Cancel</Button><Button onClick={save} busy={saving}>Save private draft</Button></div>}>
      <div className="preview-sheet-content website-editor-fields">
        <div className="website-editor-intro"><span>{page.type.replaceAll("_", " ")}</span><p>{page.description}</p></div>
        <div className="website-design-fields"><div className="preview-field"><label htmlFor="website-template">Template</label><select id="website-template" value={draft.template_key} onChange={(event) => setDraft((current) => ({ ...current, template_key: event.target.value }))}><option value="MINIMAL">Minimal</option><option value="EDITORIAL">Editorial</option><option value="AGENDA">Agenda</option></select></div><div className="preview-field"><label htmlFor="website-density">Spacing</label><select id="website-density" value={draft.responsive.density || "COMFORTABLE"} onChange={(event) => setDraft((current) => ({ ...current, responsive: { ...current.responsive, density: event.target.value as "COMFORTABLE" | "COMPACT" } }))}><option value="COMFORTABLE">Comfortable</option><option value="COMPACT">Compact</option></select></div></div>
        <div className="website-design-fields"><div className="preview-field"><label htmlFor="website-alignment">Hero alignment</label><select id="website-alignment" value={draft.responsive.hero_alignment || "LEFT"} onChange={(event) => setDraft((current) => ({ ...current, responsive: { ...current.responsive, hero_alignment: event.target.value as "LEFT" | "CENTER" } }))}><option value="LEFT">Left</option><option value="CENTER">Centered</option></select></div>{selectedSection ? <div className="preview-field"><label>Page order</label><div className="website-order-actions"><button type="button" onClick={() => moveSelected(-1)} disabled={selectedIndex <= 0}><ArrowUp size={15} />Earlier</button><button type="button" onClick={() => moveSelected(1)} disabled={selectedIndex >= draft.sections.length - 1}><ArrowDown size={15} />Later</button></div></div> : <div />}</div>
        <div className="preview-field"><label htmlFor="website-page-title">Page title</label><input id="website-page-title" value={title} onChange={(event) => setTitle(event.target.value)} maxLength={180} /></div>
        <div className="preview-field"><label htmlFor="website-page-content">Page content</label><textarea id="website-page-content" value={body} onChange={(event) => setBody(event.target.value)} rows={7} maxLength={4000} /></div>
        <div className="preview-field"><label htmlFor="website-page-items">List items <span>one per line</span></label><textarea id="website-page-items" value={items} onChange={(event) => setItems(event.target.value)} rows={4} maxLength={3000} /></div>
        <details className="website-custom-link"><summary>Optional external link</summary><div className="preview-field"><label htmlFor="website-cta-label">Button label</label><input id="website-cta-label" value={ctaLabel} onChange={(event) => setCtaLabel(event.target.value)} maxLength={80} /></div><div className="preview-field"><label htmlFor="website-cta-url">HTTPS address</label><input id="website-cta-url" type="url" value={ctaUrl} onChange={(event) => setCtaUrl(event.target.value)} placeholder="https://" maxLength={500} /></div></details>
        <div className="website-live-guidance"><Globe2 size={17} aria-hidden="true" /><p><strong>Live workflow data is never copied into this page.</strong><span>Dates, venue, submission access, registration access, and the published programme are resolved from their authoritative Conferis records.</span></p></div>
        {error ? <p className="form-error" role="alert">{error}</p> : null}
      </div>
    </ResponsiveSheet>
    <ResponsiveSheet open={Boolean(confirmation)} layout="modal" title={confirmation === "publish" ? "Publish website?" : "Unpublish website?"} onClose={() => setConfirmation(null)} footer={<div className="preview-sheet-actions"><Button variant="secondary" onClick={() => setConfirmation(null)}>Cancel</Button><Button variant={confirmation === "unpublish" ? "danger" : "primary"} busy={acting} onClick={changePublication}>{confirmation === "publish" ? "Publish now" : "Unpublish now"}</Button></div>}><div className="preview-sheet-content"><p><strong>{confirmation === "publish" ? "Make this saved version public" : "Remove the public website"}</strong></p><span>{confirmation === "publish" ? "Visitors will see this exact immutable version. Later edits remain private until published again." : "The public link will stop working immediately. Your content and version history remain saved."}</span>{error ? <p className="form-error" role="alert">{error}</p> : null}</div></ResponsiveSheet>
    <ResponsiveSheet open={Boolean(restoreVersion)} layout="modal" title={restoreVersion ? `Restore version ${restoreVersion.version}?` : "Restore website version?"} onClose={() => setRestoreVersion(null)} footer={<div className="preview-sheet-actions"><Button variant="secondary" onClick={() => setRestoreVersion(null)}>Cancel</Button><Button busy={acting} onClick={restore}>Restore as draft</Button></div>}><div className="preview-sheet-content"><p><strong>Create a new draft from this historical version</strong></p><span>The current live website will not change. Conferis will copy the selected version into a new private, editable version and retain the full history.</span>{error ? <p className="form-error" role="alert">{error}</p> : null}</div></ResponsiveSheet>
  </PreviewFrame>;
}
